import HelpVideo from "../../components/HelpVideo"
import CircularProgress from "@material-ui/core/CircularProgress"
import Paper from "@material-ui/core/Paper"
import Typography from "@material-ui/core/Typography"
import Button from "@material-ui/core/Button"
import Box from "@material-ui/core/Box"
import { useHistory } from "react-router-dom"
import { makeStyles } from "@material-ui/core"
import Card from "@material-ui/core/Card"
import CardContent from "@material-ui/core/CardContent"
import CardActions from "@material-ui/core/CardActions"
import { useHelpVideo } from "../../hooks/use-help-video"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { paths } from "../../paths"
import { useI18n } from "../../hooks"

const useStyles = makeStyles((theme) => ({
  skipActionsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
  skipCard: {
    flex: 1,
    minWidth: 220,
    maxWidth: 320,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  skipCardTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  skipCardContent: {
    flexGrow: 1,
    textAlign: "left",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  container: {
    width: "100%",
    maxWidth: 650,
  },
  paper: {
    padding: 32,
  },
  caption: {
    marginTop: 32,
    marginBottom: 32,
    maxWidth: 600,
    textAlign: "center",
  },
}))

const defaultTranslations = {
  pageTitle: "Welcome to your Organization",
  pageSubtitle: "Here's an introductory video to help you get started.",
  errorLoadingVideo: "Error loading video.",
  readyToDiveIn: "Ready to dive in? Jump straight into action below!",
  inviteColleagueTitle: "Invite a Colleague",
  inviteColleagueDesc: "Invite your teammates to collaborate in your organization right away.",
  inviteColleagueBtn: "Add member",
  createProjectTitle: "Create a Project",
  createProjectDesc: "Create your first project in your new organization.",
  createProjectBtn: "Create project",
}

type Translations = typeof defaultTranslations

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t = {} } = useI18n("org-welcome-page")

  function translate(key: keyof Translations) {
    return t[key] || defaults[key]
  }

  return {
    pageTitle: translate("pageTitle"),
    pageSubtitle: translate("pageSubtitle"),
    errorLoadingVideo: translate("errorLoadingVideo"),
    readyToDiveIn: translate("readyToDiveIn"),
    inviteColleagueTitle: translate("inviteColleagueTitle"),
    inviteColleagueDesc: translate("inviteColleagueDesc"),
    inviteColleagueBtn: translate("inviteColleagueBtn"),
    createProjectTitle: translate("createProjectTitle"),
    createProjectDesc: translate("createProjectDesc"),
    createProjectBtn: translate("createProjectBtn"),
  }
}

const OrgWelcomeRoute = () => {
  const { video, isLoading, isError } = useHelpVideo("organisational")
  const classes = useStyles()
  const history = useHistory()
  const { createPathWithContext } = useUrlWithContext()
  const translations = useTranslations(defaultTranslations)

  const handleCreateProject = () => {
    const newProjectPath = createPathWithContext(paths.newProject())
    history.push(newProjectPath)
  }

  const handleInviteColleague = () => {
    const newMemberPath = createPathWithContext(paths.orgNewMember())
    history.push(newMemberPath)
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Paper className={classes.paper} elevation={4}>
          <Typography variant="h4" gutterBottom>
            {translations.pageTitle}
          </Typography>
          <Typography variant="body1" gutterBottom color="textSecondary">
            {translations.pageSubtitle}
          </Typography>
          {isLoading ? (
            <CircularProgress />
          ) : isError ? (
            <p>{translations.errorLoadingVideo}</p>
          ) : (
            <HelpVideo video={video} />
          )}
        </Paper>
      </div>
      <Typography variant="caption" color="textSecondary" className={classes.caption}>
        {translations.readyToDiveIn}
      </Typography>
      <Box className={classes.skipActionsContainer}>
        <Card className={classes.skipCard} variant="outlined" elevation={0}>
          <CardContent className={classes.skipCardContent}>
            <Typography variant="subtitle1" className={classes.skipCardTitle}>
              {translations.inviteColleagueTitle}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {translations.inviteColleagueDesc}
            </Typography>
          </CardContent>
          <CardActions>
            <Button color="primary" onClick={handleInviteColleague}>
              {translations.inviteColleagueBtn}
            </Button>
          </CardActions>
        </Card>
        <Card className={classes.skipCard} variant="outlined" elevation={0}>
          <CardContent className={classes.skipCardContent}>
            <Typography variant="subtitle1" className={classes.skipCardTitle}>
              {translations.createProjectTitle}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {translations.createProjectDesc}
            </Typography>
          </CardContent>
          <CardActions>
            <Button color="primary" onClick={handleCreateProject}>
              {translations.createProjectBtn}
            </Button>
          </CardActions>
        </Card>
      </Box>
    </div>
  )
}

export default OrgWelcomeRoute
